import React from "react";
import { useTranslation } from "react-i18next";
import { LandingPageLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";

export function PricingPageScreen() {
  const { t } = useTranslation();
  
  const plans = [
    {
      name: t("pricing.free.name"),
      price: "$0",
      description: t("pricing.free.description"),
      features: [
        t("pricing.free.feature1"),
        t("pricing.free.feature2"),
        t("pricing.free.feature3"),
        t("pricing.free.feature4"),
        t("pricing.free.feature5")
      ],
      cta: t("pricing.free.cta"),
      href: "/register",
      popular: false
    },
    {
      name: t("pricing.basic.name"),
      price: "$4.99",
      period: t("pricing.perMonth"),
      description: t("pricing.basic.description"),
      features: [
        t("pricing.basic.feature1"),
        t("pricing.basic.feature2"),
        t("pricing.basic.feature3"),
        t("pricing.basic.feature4"),
        t("pricing.basic.feature5"),
        t("pricing.basic.feature6")
      ],
      cta: t("pricing.basic.cta"),
      href: "/register?plan=basic",
      popular: true
    },
    {
      name: t("pricing.pro.name"),
      price: "$9.99",
      period: t("pricing.perMonth"),
      description: t("pricing.pro.description"),
      features: [
        t("pricing.pro.feature1"),
        t("pricing.pro.feature2"),
        t("pricing.pro.feature3"),
        t("pricing.pro.feature4"),
        t("pricing.pro.feature5"),
        t("pricing.pro.feature6"),
        t("pricing.pro.feature7")
      ],
      cta: t("pricing.pro.cta"),
      href: "/register?plan=pro",
      popular: false
    }
  ];

  return (
    <LandingPageLayout>
      <div className="py-20 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="secondary" className="mb-4">
              {t("pricing.badge")}
            </Badge>
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl mb-4">
              {t("pricing.title")}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              {t("pricing.subtitle")}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <Card key={index} className={`flex flex-col relative ${plan.popular ? 'border-primary shadow-xl scale-105' : 'border-gray-200 dark:border-gray-700 shadow-lg'}`}>
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <Badge className="bg-primary text-primary-foreground px-3 py-1">{t("pricing.mostPopular")}</Badge>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="mb-6">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.period && <span className="text-gray-500 dark:text-gray-400">{plan.period}</span>}
                  </div>
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-600 dark:text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className={`w-full ${plan.popular ? '' : 'variant-outline'}`} variant={plan.popular ? "default" : "outline"} asChild>
                    <Link to={plan.href}>{plan.cta}</Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          <div className="mt-20 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {t("pricing.haveQuestions")} <a href="/contact" className="text-primary hover:underline">{t("pricing.contactSupport")}</a>.
            </p>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
